import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Separator } from '@cuemath/comps';
import { useGetTickets, usePatchAgentDetails } from 'actions/support-agent';
import { ERROR_AUTO_CLEAR_MS, TICKET_SOURCE, DEFAULT_SORT } from '../../constants';
import {
  ListScroll,
  TicketCard,
  TicketCardHead,
  TicketAvatar,
  TicketName,
  UnreadBadge,
  TicketSubject,
  TicketFoot,
  StatusBadge,
  TicketTime,
  ListFooter,
  StatusBtn,
  LoadMoreBtn,
  OfflineConfirm,
  OfflineConfirmActions,
  BtnSecondary,
  BtnPrimary,
} from '../../styled-comps';
import {
  TicketListInner,
  AvailabilityLabel,
  WarningText,
  AgentBadge,
  TeamBadge,
  TicketMetaRow,
  SearchInputWrap,
  SearchSortRow,
  SearchTextInput,
  EmptySearch,
  EmptyTitle,
  EmptySub,
  getTicketUser,
  getTicketInitials,
  getTicketSubject,
  getTicketUnread,
  getTicketWait,
  getTicketAssignedTo,
  getTicketTeam,
} from './styled';
import { getCookie } from 'utils';
import InboxFilters from './filters';
import SortControl from './sort-control';
import Icon from '../icon';

const InboxView = ({ activeTicketId, onPickTicket, isLead, isOnline, readTicketIds }) => {
  const [online, setOnline] = useState(Boolean(isOnline));
  useEffect(() => {
    setOnline(Boolean(isOnline));
  }, [isOnline]);
  const [showOfflineConfirm, setShowOfflineConfirm] = useState(false);
  const [accumulatedTickets, setAccumulatedTickets] = useState([]);
  const currentPageRef = useRef(1);
  const loadMoreRef = useRef(null);

  const [statusError, setStatusError] = useState(null);
  const [ticketSearch, setTicketSearch] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);

  const {
    data: ticketsData,
    getAll: getTickets,
    isProcessing: ticketsProcessing,
    isProcessingFailed: ticketsFailed,
    isStale: ticketsStale,
  } = useGetTickets();
  const lastQueryRef = useRef(null);
  const {
    patch: patchAgentStatus,
    isProcessing: statusProcessing,
  } = usePatchAgentDetails({
    onComplete: error => {
      if (error) {
        setOnline(prev => !prev);
        setStatusError(typeof error === 'string' ? error : 'Failed to update status');
        setTimeout(() => setStatusError(null), ERROR_AUTO_CLEAR_MS);
      }
    },
  });

  const handleQueryChange = useCallback(
    (queryParams, page) => {
      if (queryParams) {
        currentPageRef.current = page || 1;
        const q = { ...queryParams, sort_by: sort.sort_by, sort_order: sort.sort_order };
        lastQueryRef.current = q;
        getTickets(q);
      }
    },
    [getTickets, sort],
  );

  // Sort is a session toggle applied to the current inbox view — re-run the
  // last filter query with the new order. It is never written into a saved
  // filter (InboxFilters' queryParams never sees it).
  const handleSortChange = useCallback(
    option => {
      setSort(option);
      currentPageRef.current = 1;
      const base = lastQueryRef.current || { caller_type: 'agent', limit: 50 };
      const q = {
        ...base,
        page: 1,
        sort_by: option.sort_by,
        sort_order: option.sort_order,
      };
      lastQueryRef.current = q;
      getTickets(q);
    },
    [getTickets],
  );

  // Re-fetch when the cache is invalidated (e.g. PubNub events on the agent
  // dashboard channel call invalidateTickets()). Replays the last query at
  // page 1 so the accumulator effect below resets the list cleanly.
  useEffect(() => {
    if (ticketsStale && !ticketsProcessing && lastQueryRef.current) {
      currentPageRef.current = 1;
      getTickets(lastQueryRef.current);
    }
  }, [ticketsStale, ticketsProcessing, getTickets]);

  // Ticket ID search runs on Enter (agents paste a UUID, they don't type it).
  // It bypasses active filters so a matching ticket is found regardless of
  // status, assignment, or other filter state.
  const runTicketSearch = useCallback(() => {
    const term = ticketSearch.trim();
    // Guard repeated Enter: skip while a fetch is already in flight.
    if (!term || ticketsProcessing) return;
    currentPageRef.current = 1;
    getTickets({
      caller_type: 'agent',
      search: term,
      page: 1,
      limit: 50,
      sort_by: sort.sort_by,
      sort_order: sort.sort_order,
    });
  }, [ticketSearch, ticketsProcessing, getTickets, sort]);

  // Clearing the box restores the normal filter-based list.
  const handleSearchChange = useCallback(
    value => {
      setTicketSearch(value);
      if (!value && lastQueryRef.current) {
        currentPageRef.current = 1;
        getTickets({ ...lastQueryRef.current, page: 1 });
      }
    },
    [getTickets],
  );

  useEffect(() => {
    const list = ticketsData?.tickets;
    if (!Array.isArray(list)) return;
    if (currentPageRef.current === 1) {
      setAccumulatedTickets(list);
    } else {
      setAccumulatedTickets(prev => [...prev, ...list]);
    }
  }, [ticketsData]); // eslint-disable-line react-hooks/exhaustive-deps

  const tickets = accumulatedTickets;
  const totalTickets = ticketsData?.total || tickets.length;
  const hasMore = ticketsData?.has_next || false;

  const agentUserId = getCookie('user_id') || getCookie('emp_id');
  const updateStatus = useCallback(
    goOnline => {
      setOnline(goOnline);
      setShowOfflineConfirm(false);
      patchAgentStatus(agentUserId, {
        action: 'update_available_status',
        status: goOnline ? 'ONLINE' : 'OFFLINE',
      });
    },
    [patchAgentStatus, agentUserId],
  );

  const handleStatusToggle = useCallback(() => {
    if (online) {
      setShowOfflineConfirm(true);
    } else {
      updateStatus(true);
    }
  }, [online, updateStatus]);

  const handleCancelOffline = useCallback(() => setShowOfflineConfirm(false), []);

  return (
    <>
      <InboxFilters
        onQueryChange={handleQueryChange}
        totalTickets={totalTickets}
        loadMoreRef={loadMoreRef}
      />
      <Separator height={8} transparent />
      <SearchSortRow>
        <SearchInputWrap>
          <Icon name="search" size={16} />
          <SearchTextInput
            placeholder="Search by ticket ID..."
            value={ticketSearch}
            onChange={e => handleSearchChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runTicketSearch()}
          />
        </SearchInputWrap>
        <SortControl value={sort.value} onChange={handleSortChange} />
      </SearchSortRow>
      <ListScroll>
        {ticketsFailed && tickets.length === 0 && (
          <WarningText>Failed to load tickets. Please try again.</WarningText>
        )}
        {!ticketsProcessing && !ticketsFailed && tickets.length === 0 && (
          <EmptySearch>
            {ticketSearch.trim() ? (
              <>
                <EmptyTitle>No ticket found</EmptyTitle>
                <EmptySub>Check the ticket ID and try again</EmptySub>
              </>
            ) : (
              <EmptyTitle>No tickets here</EmptyTitle>
            )}
          </EmptySearch>
        )}
        <TicketListInner>
          {tickets.map((ticket, index) => (
            <React.Fragment key={ticket.id}>
              {index !== 0 && <Separator height={8} transparent />}
              <TicketCard
                $status={ticket.status}
                $active={ticket.id === activeTicketId}
                onClick={() => onPickTicket(ticket)}
              >
                <TicketCardHead>
                  <TicketAvatar>{getTicketInitials(ticket)}</TicketAvatar>
                  <TicketName>{getTicketUser(ticket)}</TicketName>
                  {getTicketUnread(ticket) > 0 && !readTicketIds?.has(ticket.id) && (
                    <UnreadBadge />
                  )}
                </TicketCardHead>
                <TicketSubject>{getTicketSubject(ticket)}</TicketSubject>
                <TicketFoot>
                  <StatusBadge $status={ticket.status}>
                    {ticket.status || 'Open'}
                  </StatusBadge>
                  {ticket.source === TICKET_SOURCE.EMAIL && (
                    <StatusBadge $status="EMAIL">Email</StatusBadge>
                  )}
                  {ticket.source === TICKET_SOURCE.INTERNAL && (
                    <StatusBadge $status="INTERNAL">Internal</StatusBadge>
                  )}
                  <TicketTime>{getTicketWait(ticket)}</TicketTime>
                </TicketFoot>
                {((isLead && getTicketAssignedTo(ticket)) || getTicketTeam(ticket)) && (
                  <TicketMetaRow>
                    {isLead && getTicketAssignedTo(ticket) && (
                      <AgentBadge>
                        <Icon name="user" size={10} />
                        {getTicketAssignedTo(ticket)}
                      </AgentBadge>
                    )}
                    {getTicketTeam(ticket) && (
                      <TeamBadge>{getTicketTeam(ticket)}</TeamBadge>
                    )}
                  </TicketMetaRow>
                )}
              </TicketCard>
            </React.Fragment>
          ))}
          {hasMore && (
            <LoadMoreBtn onClick={() => loadMoreRef.current?.()}>
              Load more tickets
            </LoadMoreBtn>
          )}
        </TicketListInner>
      </ListScroll>

      <ListFooter>
        {statusError && <WarningText>{statusError}</WarningText>}
        {!showOfflineConfirm ? (
          <>
            <AvailabilityLabel variant="body2">Your availability</AvailabilityLabel>
            <StatusBtn
              $online={online}
              onClick={handleStatusToggle}
              disabled={statusProcessing}
            >
              {online ? 'Online' : 'Offline'}
            </StatusBtn>
          </>
        ) : (
          <OfflineConfirm>
            <WarningText variant="body2">
              Going offline will stop new ticket assignments. Active tickets remain with
              you.
            </WarningText>
            <OfflineConfirmActions>
              <BtnSecondary onClick={handleCancelOffline}>Cancel</BtnSecondary>
              <BtnPrimary onClick={() => updateStatus(false)}>Confirm</BtnPrimary>
            </OfflineConfirmActions>
          </OfflineConfirm>
        )}
      </ListFooter>
    </>
  );
};

export default InboxView;
